import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

// Import necessary Ant Design components and icons
import { EditOutlined, PlusOutlined } from "@ant-design/icons";

// Import necessary drag-and-drop utilities
import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";

import "../../../styles/bulletinAdmin/components/bulletinBlock.scss";

// Import all block components

import ImageBlock from "../blocks/ImageBlock";
import VideoBlock from "../blocks/VideoBlock";
import FileBlock from "../blocks/FileBlock";
import TextBlock from "../blocks/TextBlock";

// Import block menu
import BlockInsertMenu from "../EbulletinBlockMenu";
import ActionMenu from "../EbulletinActionMenu";

// Scroll monitor component to handle auto-scrolling during drag-and-drop.
const ScrollMonitor = () => {
  useEffect(() => {
    // Initialize auto-scroll for the document element
    return autoScrollForElements({
      element: document.documentElement,
      canScroll: ({ source }) => source.data.type === "block-card",
    });
  }, []);

  return null;
};

export default function BlockCard({
  block,
  sectionIdx,
  blockIdx,
  sections,
  setSections,
  isEditing,
  onClick,
  moveBlock,
  index,
  totalItems = 0,
  handleInsertBlock,
  handleDeleteBlock,
}) {
  // Define a ref for the block element and state for drag-and-drop.
  const ref = useRef(null);
  // State to manage the drag-and-drop state and toolbar visibility
  const [state, setState] = useState({ type: "idle" });
  // State to manage visibility of the floating toolbar
  const [toolbarVisible, setToolbarVisible] = useState(false);

  // Define styles for different states
  const stateStyles = {
    idle: { cursor: "default", opacity: 1, transform: "none" },
    dragging: { cursor: "grabbing", opacity: 0.5, transform: "none" },
    preview: { cursor: "grabbing", opacity: 0.5 },
  };

  // Function to get the display name for the block type
  const getBlockTypeDisplay = (blockData) => {
    const typeMap = {
      text: "Text",
      image: "Image",
      video: "Video",
      file: "File",
    };
    if (blockData?.layout === "two-column") return "Two-Column Block";
    return typeMap[blockData?.type] || "Undefined Block";
  };

  // Effect to handle drag and drop functionality
  useEffect(() => {
    if (!isEditing) return;
    const element = ref.current;
    if (!element) return;

    return combine(
      draggable({
        element,
        getInitialData: () => ({ id: block.id, index, type: "block-card" }),

        // Handle auto-scroll for the block element
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setState({ type: "preview" });
          const dragPreview = document.createElement("div");
          dragPreview.className = "drag-preview";
          dragPreview.textContent = getBlockTypeDisplay(block);
          document.body.appendChild(dragPreview);
          nativeSetDragImage(dragPreview, 100, 20);
          requestAnimationFrame(() => {
            if (document.body.contains(dragPreview)) {
              document.body.removeChild(dragPreview);
            }
          });
        },

        // Handle drag start and end events
        onDragStart: () => {
          setState({ type: "dragging" });
          setToolbarVisible(false);
        },
        onDrop: () => setState({ type: "idle" }),
      }),

      // Handle drop target functionality
      dropTargetForElements({
        element,
        canDrop: ({ source }) => source.data.type === "block-card",
        onDragEnter: () => setState((s) => ({ ...s, isDropTarget: true })),
        onDragLeave: () => setState((s) => ({ ...s, isDropTarget: false })),
        onDrop: ({ source }) => {
          setState({ type: "idle" });
          if (source.data.index !== index) {
            moveBlock(source.data.index, index);
          }
        },
      })
    );
  }, [block.id, index, blockIdx, moveBlock, isEditing, block]);

  // Handle two-column layout creation
  const handleTwoColumn = () => {
    const updated = [...sections];
    const existingBlock = updated[sectionIdx].blocks[blockIdx];

    // Ensure the existing block is not already a two-column block
    updated[sectionIdx].blocks[blockIdx] = {
      id: existingBlock.id,
      layout: "two-column",
      type: "two-column",
      leftBlock: {
        ...existingBlock,
        id: `${existingBlock.id}-left`,
        type: "text",
        content: "",
        style: existingBlock.style || {},
      },
      rightBlock: {
        id: `${existingBlock.id}-right`,
        type: "text",
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        style: {},
      },
    };
    setSections(updated);
  };

  // This function updates properties of a block, including media-specific properties
  // and ensures backward compatibility with text blocks.
  const updateBlockProperty = (prop, value, column = null) => {
    const updated = [...sections];
    let targetBlock;

    if (column) {
      targetBlock = updated[sectionIdx].blocks[blockIdx][`${column}Block`];
    } else {
      targetBlock = updated[sectionIdx].blocks[blockIdx];
    }

    if (targetBlock) {
      targetBlock[prop] = value;

      // For media blocks, ensure we maintain all necessary properties
      if (["image", "video", "file"].includes(targetBlock.type)) {
        // Initialize media-specific properties if they don't exist
        if (!targetBlock.hasOwnProperty("url")) targetBlock.url = "";
        if (!targetBlock.hasOwnProperty("fileName")) targetBlock.fileName = "";
        if (!targetBlock.hasOwnProperty("fileSize")) targetBlock.fileSize = 0;
        if (!targetBlock.hasOwnProperty("mimeType")) targetBlock.mimeType = "";

        // Type-specific properties
        if (targetBlock.type === "image") {
          if (!targetBlock.hasOwnProperty("alt")) targetBlock.alt = "";
          if (!targetBlock.hasOwnProperty("caption")) targetBlock.caption = "";
        }
        if (targetBlock.type === "video") {
          if (!targetBlock.hasOwnProperty("title")) targetBlock.title = "";
          if (!targetBlock.hasOwnProperty("thumbnail"))
            targetBlock.thumbnail = "";
        }
      }

      setSections(updated);
    }
  };

  // Function to update the style of a block, either for the entire block or a specific column
  const updateBlockStyle = (newStyles, column = null) => {
    const updated = [...sections];
    let targetBlock;

    if (column) {
      targetBlock = updated[sectionIdx].blocks[blockIdx][`${column}Block`];
    } else {
      targetBlock = updated[sectionIdx].blocks[blockIdx];
    }

    if (targetBlock) {
      targetBlock.style = { ...(targetBlock.style || {}), ...newStyles };
      setSections(updated);
    }
  };

  // Universal content update function for backward compatibility with text blocks
  const updateColumnType = (newType, column) => {
    const updated = [...sections];
    const columnKey = `${column}Block`;
    const targetBlock = updated[sectionIdx].blocks[blockIdx][columnKey];

    if (targetBlock) {
      // Create a fresh block object based on type
      const baseBlock = {
        id: targetBlock.id,
        type: newType,
        style: targetBlock.style || {},
      };

      // Add type-specific properties
      if (["image", "video", "file"].includes(newType)) {
        baseBlock.url = "";
        baseBlock.fileName = "";
        baseBlock.fileSize = 0;
        baseBlock.mimeType = "";

        if (newType === "image") {
          baseBlock.alt = "";
          baseBlock.caption = "";
        }
        if (newType === "video") {
          baseBlock.title = "";
          baseBlock.thumbnail = "";
        }
      } else {
        // Text blocks
        baseBlock.content = "";
      }

      updated[sectionIdx].blocks[blockIdx][columnKey] = baseBlock;
      setSections(updated);
    }
  };

  // Function to render the block based on its type
  const renderBlockByType = (blockData, column = null) => {
    // If a column block is empty or has no type, show a placeholder
    if (!blockData || !blockData.type) {
      return (
        <div className="empty-block-placeholder">
          {isEditing ? "Click 'Change Type' to add content" : ""}
        </div>
      );
    }

    // Prepare common props for all block types
    const blockProps = {
      block: blockData,
      isEditing,
      updateBlockProperty: (prop, value) =>
        updateBlockProperty(prop, value, column),
      updateBlockStyle: (newStyles) => updateBlockStyle(newStyles, column),
      updateBlockContent: (content) =>
        updateBlockProperty("content", content, column),
      bulletinId: sections[sectionIdx]?.bulletin_id,
      sectionId: sections[sectionIdx]?.id,
    };

    switch (blockData.type) {
      case "image":
        return <ImageBlock {...blockProps} />;
      case "video":
        return <VideoBlock {...blockProps} />;
      case "file":
        return <FileBlock {...blockProps} />;
      case "text":
      default:
        return <TextBlock {...blockProps} />;
    }
  };

  // Render the block card with drag and drop functionality
  const renderBlockContent = () => {
    if (block.layout === "two-column") {
      return (
        <div className="two-column-layout">
          <div className="column left-column">
            {isEditing && (
              <div className="column-header">
                <BlockInsertMenu
                  onSelect={(type) => updateColumnType(type, "left")}
                  buttonProps={{
                    size: "small",
                    type: "text",
                    children: "Change Type",
                  }}
                  placement="bottomRight"
                  icon={<EditOutlined />}
                />
              </div>
            )}
            <div className="column-content">
              {renderBlockByType(block.leftBlock, "left")}
            </div>
          </div>
          <div className="column right-column">
            {isEditing && (
              <div className="column-header">
                <BlockInsertMenu
                  onSelect={(type) => updateColumnType(type, "right")}
                  buttonProps={{
                    size: "small",
                    type: "text",
                    children: "Change Type",
                  }}
                  placement="bottomLeft"
                  icon={<EditOutlined />}
                />
              </div>
            )}
            <div className="column-content">
              {renderBlockByType(block.rightBlock, "right")}
            </div>
          </div>
        </div>
      );
    }
    return renderBlockByType(block);
  };

  if (state.type === "dragging") return null;

  return (
    <div
      className={`block-container ${!isEditing ? "view-only" : ""}`}
      onMouseEnter={() => isEditing && setToolbarVisible(true)}
      onMouseLeave={() => setToolbarVisible(false)}
    >
      {/* Render the scroll monitor for auto-scrolling during drag-and-drop */}
      {isEditing && ReactDOM.createPortal(<ScrollMonitor />, document.body)}

      {state.isDropTarget && isEditing && (
        <div className="drop-indicator">
          <div className="drop-line"></div>
        </div>
      )}
      <div
        ref={ref}
        className={`block-wrapper ${state.type} ${
          state.isDropTarget ? "drop-target" : ""
        } ${isEditing ? "edit-mode" : ""} ${
          block.layout === "two-column" ? "two-column-wrapper" : ""
        }`}
        onClick={state.type !== "dragging" ? onClick : undefined}
        style={{ ...stateStyles[state.type], transition: "all 0.2s ease" }}
      >
        {isEditing && (
          <div
            className={`floating-block-toolbar ${
              toolbarVisible ? "visible" : ""
            }`}
          >
            <div className="toolbar-group">
              <BlockInsertMenu
                currentIndex={index}
                handleInsertBlock={handleInsertBlock}
                icon={<PlusOutlined />}
              />
              <ActionMenu
                block={block}
                index={index}
                totalItems={totalItems}
                handleTwoColumn={handleTwoColumn}
                handleDeleteBlock={handleDeleteBlock}
              />
            </div>
          </div>
        )}
        <div className={`block-content ${!isEditing ? "view-only" : ""}`}>
          {renderBlockContent()}
        </div>
      </div>
    </div>
  );
}
